import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useBranding } from '../../contexts/BrandingContext';
import { 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  ClipboardList, 
  User, 
  Settings, 
  Sparkles,
  X,
  Check,
  Trash2,
  Filter,
  MoreHorizontal,
  Clock,
  ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isOpen, 
    markAsRead, 
    markAllAsRead, 
    removeNotification, 
    clearAllNotifications,
    closeNotificationCenter 
  } = useNotifications();
  const { branding } = useBranding();
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const iconMap = {
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    ClipboardList,
    User,
    Settings,
    Sparkles
  };

  const typeConfig = {
    success: { 
      icon: CheckCircle, 
      color: 'text-success', 
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20'
    },
    error: { 
      icon: AlertCircle, 
      color: 'text-error', 
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20'
    },
    warning: { 
      icon: AlertTriangle, 
      color: 'text-warning', 
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20'
    },
    info: { 
      icon: Info, 
      color: 'text-info', 
      bgColor: 'bg-info/10',
      borderColor: 'border-info/20'
    },
    task: { 
      icon: ClipboardList, 
      color: 'text-brand-primary', 
      bgColor: 'bg-brand-primary/10',
      borderColor: 'border-brand-primary/20'
    },
    client: { 
      icon: User, 
      color: 'text-brand-secondary', 
      bgColor: 'bg-brand-secondary/10',
      borderColor: 'border-brand-secondary/20'
    },
    system: { 
      icon: Settings, 
      color: 'text-muted-foreground', 
      bgColor: 'bg-muted',
      borderColor: 'border-border'
    },
    ai: { 
      icon: Sparkles, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-200'
    }
  };

  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter(notification => notification.type === filter);
    }

    // Sort by timestamp
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else {
        return new Date(a.timestamp) - new Date(b.timestamp);
      }
    });

    return filtered;
  }, [notifications, filter, sortBy]);

  const groupedNotifications = useMemo(() => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    filteredNotifications.forEach(notification => {
      const notificationDate = new Date(notification.timestamp);
      
      if (notificationDate >= today) {
        groups.today.push(notification);
      } else if (notificationDate >= yesterday) {
        groups.yesterday.push(notification);
      } else if (notificationDate >= thisWeek) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification) => {
    console.log('[NotificationCenter] Notification clicked:', { id: notification.id, hasAction: !!notification.action, hasOnClick: !!notification.onClick });
    
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Check for both action and onClick (onClick is from createNotification options)
    const clickHandler = notification.action || notification.onClick;
    if (clickHandler) {
      console.log('[NotificationCenter] Calling click handler');
      // Close notification center before navigating
      closeNotificationCenter();
      clickHandler();
    } else {
      console.log('[NotificationCenter] No click handler found for notification');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-start justify-end pt-16 pr-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md animate-in slide-in-from-right-5 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            {unreadCount > 0 && (
              <span 
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: branding.primaryColor }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={markAllAsRead}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              title="Mark all as read"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={closeNotificationCenter}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['all', 'task', 'client', 'system', 'ai'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  filter === type
                    ? 'text-white'
                    : 'text-muted-foreground bg-muted hover:bg-muted/80'
                )}
                style={filter === type ? { backgroundColor: branding.primaryColor } : {}}
              >
                {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto">
          {Object.entries(groupedNotifications).map(([groupName, groupNotifications]) => {
            if (groupNotifications.length === 0) return null;

            return (
              <div key={groupName} className="p-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  {groupName === 'today' ? 'Today' : 
                   groupName === 'yesterday' ? 'Yesterday' :
                   groupName === 'thisWeek' ? 'This Week' : 'Older'}
                </h3>
                <div className="space-y-3">
                  {groupNotifications.map((notification) => {
                    const config = typeConfig[notification.type] || typeConfig.info;
                    const Icon = config.icon;

                    return (
                      <div
                        key={notification.id}
                        className={cn(
                          'flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted',
                          !notification.read && 'bg-blue-50/50 dark:bg-blue-950/10'
                        )}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className={cn(
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                          config.bgColor,
                          config.borderColor,
                          'border'
                        )}>
                          <Icon className={cn('h-4 w-4', config.color)} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={cn(
                                'text-sm font-medium',
                                !notification.read ? 'text-foreground' : 'text-foreground'
                              )}>
                                {notification.title}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {notification.message}
                              </p>
                              <div className="flex items-center space-x-2 mt-2">
                                <span className="text-xs text-muted-foreground flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {formatTime(notification.timestamp)}
                                </span>
                                {(notification.action || notification.onClick) && (
                                  <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Click to view
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              {!notification.read && (
                                <div 
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: branding.primaryColor }}
                                />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeNotification(notification.id);
                                }}
                                className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {filteredNotifications.length === 0 && (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">
                {filter === 'all' ? 'You\'re all caught up!' : `No ${filter} notifications`}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-4 border-t border-border">
            <button
              onClick={clearAllNotifications}
              className="w-full text-sm text-muted-foreground hover:text-red-600 transition-colors"
            >
              Clear all notifications
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;